import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useMsal } from "@azure/msal-react";
import * as microsoftTeams from "@microsoft/teams-js";
import { useAuth } from "../../../api/grpc/auth/useAuth";
import { useAuthContext } from "../../../lib/context/Auth/AuthContext";
import { loginRequest, msalConfig } from "../authConfig";
import { InteractionStatus, AccountInfo } from "@azure/msal-browser";
import {
  buildTeamsO365AuthStartUrl,
  TEAMS_MICROSOFT_LOGIN_AUTH_STATE,
  TEAMS_MICROSOFT_LOGIN_TOKEN_STORAGE_KEY,
} from "../../../lib/utils/teamsAuth";

const isTeamsNativeClient = async () => {
  if (!microsoftTeams.app.isInitialized()) {
    return false;
  }

  try {
    const context = await microsoftTeams.app.getContext();

    return context.app.host.clientType !== microsoftTeams.HostClientType.web;
  } catch (error) {
    return false;
  }
};

// MSAL persists an "interaction in progress" flag under the literal key
// "msal.interaction.status", so a popup attempt that errors out or gets
// abandoned mid-flow (e.g. an Azure AD error page rendered inside the
// popup) can leave it stuck. Once stuck, every future loginPopup() call
// throws interaction_in_progress immediately, without even opening a popup.
//
// This flag does NOT follow the `cacheLocation` config - MSAL's temporary
// cache (which this belongs to) always defaults to sessionStorage unless a
// separate `temporaryCacheLocation` option is set (it isn't, here). And
// because this app also sets `storeAuthStateInCookie: true`, MSAL checks a
// same-named cookie first and writes to it alongside sessionStorage. So a
// stale value can be sitting in the cookie, sessionStorage, or (from very
// old MSAL versions) localStorage - clear all three.
const INTERACTION_STATUS_KEY = "msal.interaction.status";

const clearStaleInteractionStatus = () => {
  try {
    window.sessionStorage.removeItem(INTERACTION_STATUS_KEY);
    window.localStorage.removeItem(INTERACTION_STATUS_KEY);
    document.cookie = `${INTERACTION_STATUS_KEY}=;path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  } catch (error) {
    // Ignore storage access issues (e.g. private browsing).
  }
};

const buildTeamsMicrosoftLoginUrl = () => {
  const redirectUri = msalConfig.auth.redirectUri;

  if (!redirectUri) {
    throw new Error("Microsoft redirect URI is not configured.");
  }

  const params = new URLSearchParams({
    client_id: msalConfig.auth.clientId,
    response_type: "token",
    redirect_uri: redirectUri,
    response_mode: "fragment",
    scope: loginRequest.scopes.join(" "),
    prompt: "select_account",
    state: TEAMS_MICROSOFT_LOGIN_AUTH_STATE,
  });

  return `https://login.microsoftonline.com/organizations/oauth2/v2.0/authorize?${params.toString()}`;
};

export const MicrosoftLogin = () => {
  const { instance, accounts, inProgress } = useMsal();
  const { exchangeSocialAuthToken } = useAuth();
  const { signInWithProvider, setSsoLoading } = useAuthContext();
  const [useTeamsNativeAuth, setUseTeamsNativeAuth] = useState(false);

  const loginAttemptRef = useRef(false);
  const processedAccounts = useRef<Set<string>>(new Set());

  const signInWithMicrosoftAccessToken = useCallback(
    async (accessToken: string): Promise<void> => {
      try {
        setSsoLoading(true);
        localStorage.setItem("token", accessToken);

        const { response } = await exchangeSocialAuthToken(accessToken);
        await signInWithProvider(response.token, response.customerID);
        localStorage.setItem("initiateLogin", "1");
      } catch (error: any) {
        localStorage.clear();
        const code = (error?.errorCode || error?.code || "")
          .toString()
          .toLowerCase();
        const message = (error?.message || "").toString().toLowerCase();
        const isUserCancelled =
          code === "user_cancelled" ||
          code === "popup_closed" ||
          code === "auth_cancelled" ||
          message.includes("user cancelled") ||
          message.includes("user canceled");

        if (!isUserCancelled) {
          toast.error(error?.message);
        }
      } finally {
        setTimeout(() => {
          setSsoLoading(false);
        }, 1500);
      }
    },
    [exchangeSocialAuthToken, setSsoLoading, signInWithProvider]
  );

  const performSilentSignIn = useCallback(
    async (account: AccountInfo | undefined): Promise<void> => {
      if (!account) {
        return;
      }

      const res = await instance.acquireTokenSilent({
        account,
        scopes: loginRequest.scopes,
      });

      await signInWithMicrosoftAccessToken(res.accessToken);
    },
    [instance, signInWithMicrosoftAccessToken]
  );

  useEffect(() => {
    let isMounted = true;

    isTeamsNativeClient().then((isNativeClient) => {
      if (isMounted) {
        setUseTeamsNativeAuth(isNativeClient);
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const attemptPending =
      loginAttemptRef.current ||
      sessionStorage.getItem("msalLoginAttempt") === "1";

    if (!attemptPending) {
      processedAccounts.current.clear();
      return;
    }

    if (inProgress !== InteractionStatus.None) {
      return;
    }

    if (accounts.length === 0) {
      return;
    }

    const nextAccount = accounts.find(
      (account) => !processedAccounts.current.has(account.homeAccountId)
    );

    if (!nextAccount) {
      return;
    }

    const accountId = nextAccount.homeAccountId;
    processedAccounts.current.add(accountId);

    performSilentSignIn(nextAccount)
      .catch(() => {
        processedAccounts.current.delete(accountId);
      })
      .finally(() => {
        loginAttemptRef.current = false;
        sessionStorage.removeItem("msalLoginAttempt");
        processedAccounts.current.delete(accountId);
      });
  }, [accounts, inProgress, performSilentSignIn]);

  const handleLogin = async () => {
    // Teams native app blocks window.open, so use Teams' auth window there.
    if (useTeamsNativeAuth) {
      try {
        localStorage.removeItem(TEAMS_MICROSOFT_LOGIN_TOKEN_STORAGE_KEY);

        const redirectUri = msalConfig.auth.redirectUri;
        if (!redirectUri) {
          throw new Error("Microsoft redirect URI is not configured.");
        }

        const authUrl = buildTeamsMicrosoftLoginUrl();
        const teamsAuthStartUrl = buildTeamsO365AuthStartUrl(
          redirectUri,
          authUrl
        );
        await microsoftTeams.authentication.authenticate({
          url: teamsAuthStartUrl,
          width: 600,
          height: 700,
        });

        const accessToken = localStorage.getItem(
          TEAMS_MICROSOFT_LOGIN_TOKEN_STORAGE_KEY
        );
        localStorage.removeItem(TEAMS_MICROSOFT_LOGIN_TOKEN_STORAGE_KEY);

        if (!accessToken) {
          throw new Error(
            "Microsoft authentication did not return an access token."
          );
        }

        await signInWithMicrosoftAccessToken(accessToken);
      } catch (error: any) {
        localStorage.removeItem(TEAMS_MICROSOFT_LOGIN_TOKEN_STORAGE_KEY);
        const message = (error?.message || "").toString().toLowerCase();
        const isUserCancelled =
          message.includes("cancelled") ||
          message.includes("canceled") ||
          message.includes("user cancelled") ||
          message.includes("user canceled");
        if (!isUserCancelled) {
          toast.error(error?.message);
        }
      }
      return;
    }

    // Browser: guard against MSAL not yet settled (causes popup_window_error on first load)
    if (inProgress !== InteractionStatus.None) {
      return;
    }

    // Our own MSAL state says nothing is in progress, so any persisted
    // interaction flag left over from a previous failed attempt is stale.
    clearStaleInteractionStatus();

    try {
      loginAttemptRef.current = true;
      sessionStorage.setItem("msalLoginAttempt", "1");

      const response = await instance.loginPopup({
        prompt: "select_account",
        scopes: loginRequest.scopes,
      });

      if (response?.account) {
        instance.setActiveAccount(response.account);
      }
    } catch (error: any) {
      loginAttemptRef.current = false;
      sessionStorage.removeItem("msalLoginAttempt");
      setSsoLoading(false);
      const code = (error?.errorCode || error?.code || "")
        .toString()
        .toLowerCase();
      const message = (error?.message || "").toString().toLowerCase();
      const isUserCancelled =
        code === "user_cancelled" ||
        code === "popup_closed" ||
        code === "auth_cancelled" ||
        message.includes("user cancelled") ||
        message.includes("user canceled");

      if (!isUserCancelled) {
        toast.error(error?.message);
      }
    }
  };
  return (
    <button onClick={() => handleLogin()}>
      <span className="ConnectProviders__office"></span>
      <span>Sign in with Microsoft</span>
    </button>
  );
};
